<template>
  <div class="page page-table-builder">
    <div class="page-toolbar bg-white shadow-sm p-3">
      <breadcrumb />
      <div
        v-if="table.entityGuid"
        class="d-flex justify-content-end "
      >
        <b-button
          size="sm"
          variant="outline-secondary"
          :to="{name: 'dataManagementPermissions', params: {itemId: schema.name, type: permissionItemTypes.table}}"
        >
          <font-awesome-icon :icon="['fan', 'key']" />
          Permissions
        </b-button>

        <b-button
          size="sm"
          variant="outline-primary"
          class="ml-1"
          :to="{
            name: 'dataManagementTableView',
            params: {tableId: table.entityGuid, moduleUuid: table.moduleGuid},
            query:{moduleUuid: $route.query.moduleUuid, view: $route.query.view},
          }"
        >
          <font-awesome-icon :icon="['fan', 'eye']" />
          View Data
        </b-button>

        <b-button
          size="sm"
          variant="outline-danger"
          class="ml-1"
          @click="deleteTableHandler(table)"
        >
          <font-awesome-icon :icon="['fan', 'trash']" />
          Delete Table
        </b-button>
      </div>
    </div>

    <div
      v-if="disableLoading || isLoaded()"
      class="page-content table-builder p-3"
    >
      <tabs-component
        :is-new-table="isNewTable"
        :tabs-data="tabsData"
        @get-selected-tab="getSelectedTab"
      />

      <div v-if="selectedTab === 'general'">
        <div class="row">
          <div class="col-sm-12 mb-3">
            <b-card
              no-body
              class="table-details"
              header-class="table-details-header"
            >
              <div
                slot="header"
                @click="showTableDetails = !showTableDetails"
              >
                <span>Table Details</span>
                <font-awesome-icon
                  id="tableDetailsCollapse"
                  v-b-toggle.table-details-collapse
                  class="chevron-position"
                  :icon="['fan', showTableDetails ? 'chevron-up' : 'chevron-down']"
                />
              </div>
              <b-collapse
                id="table-details-collapse"
                :visible="showTableDetails"
                class="mt-2"
              >
                <b-card-body>
                  <div
                    v-if="errors.length"
                    class="alert alert-danger"
                    :dismissible="false"
                    variant="danger"
                  >
                    <ul class="mb-0">
                      <li v-for="error of errors">
                        {{ error }}
                      </li>
                    </ul>
                  </div>
                  <div class="row">
                    <div class="col">
                      <input-widget
                        :id="'createTableDisplayName'"
                        :model="table"
                        :field="fieldsSchema.displayName"
                        @blur="onDisplayNameChange"
                      />
                    </div>
                    <div class="col">
                      <input-widget
                        :id="'createTableSelectFolder'"
                        :model="table"
                        :field="fieldsSchema.folderGuid"
                      />
                    </div>
                  </div>
                  <input-widget
                    :model="table"
                    :field="fieldsSchema.description"
                  />
                  <tags-container v-if="showTableDetails" />
                  <div class="icon__wrapper">
                    <p>Table Icon</p>
                    <vfa-picker
                      v-model="table.tableIcon"
                      :only="onlyIcons"
                      class="icon-select-input"
                    >
                      <template #activator="{ on }">
                        <b-button
                          id="selectTableIconButton"
                          variant="warning"
                          @click="on"
                        >
                          Choose Icon
                        </b-button>
                        <font-awesome-icon
                          v-if="table.tableIcon"
                          id="selectTable"
                          :icon="['fas', table.tableIcon]"
                          class="selected-icon"
                        />
                      </template>
                    </vfa-picker>
                  </div>
                  <div class="row">
                    <div class="col">
                      <input-widget
                        :id="'selectRLSField'"
                        :model="table"
                        :field="fieldsSchema.rlsField"
                      />
                    </div>
                  </div>

                  <div class="row">
                    <div class="col-sm-12 text-right">
                      <b-button
                        id="openTodoListConfig"
                        @click="openTodoListModal"
                      >
                        Configure ToDoList
                      </b-button>
                    </div>
                  </div>
                </b-card-body>
              </b-collapse>
            </b-card>
          </div>
          <div class="col-sm-12 mb-3">
            <b-card
              class="card-columns"
              header="Fields"
            >
              <div
                v-if="fieldErrors.length"
                class="alert alert-danger"
                :dismissible="false"
              >
                <ul class="mb-0">
                  <li v-for="error of fieldErrors">
                    {{ error }}
                  </li>
                </ul>
              </div>
              <ContentSpinner
                :show="fieldsLoading"
                :fullscreen="true"
              />
              <div v-if="!isNewTable">
                <div class="fields-header d-flex mb-3 mt-3 pr-4">
                  <b-button
                    id="addFieldButton"
                    @click="addField"
                  >
                    <font-awesome-icon :icon="['fan', 'plus']" />
                    Add Field
                  </b-button>
                </div>
                <div class="table-wrapper">
                  <table class="table-fields w-100">
                    <thead>
                      <tr>
                        <th class="width-25" />
                        <th class="pl-sm-2">Field Label</th>
                        <th class="pl-sm-2">Field Type</th>
                        <th class="text-right">
                          Required
                        </th>
                        <th class="text-right">
                          Display in Table
                        </th>
                        <th class="text-right">
                          Display in Form
                        </th>
                        <th class="text-right">
                          Business Key
                        </th>
                        <th class="text-right">
                          Unique
                        </th>
                        <th class="text-right pr-3">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <draggable
                      tag="tbody"
                      :list="table.fields"
                      handle=".handle"
                      @change="onDragHandler"
                    >
                      <field-item
                        v-for="(field, index) in table.fields"
                        ref="fieldItem"
                        :key="field.fieldGuid"
                        v-b-tooltip.hover
                        :is-error-during-update-exist="isErrorDuringUpdateExist"
                        :field="field"
                        :is-table-edit="!isNewTable"
                        :is-last-item="index === table.fields.length - 1"
                        :is-disabled-to-save="!checkCanSaveField(field)"
                        :save-field-prohibited-message="saveFieldProhibitedMessage(field)"
                        :is-disabled-to-delete="!checkCanDeleteField(field)"
                        :delete-field-prohibited-message="deleteFieldProhibitedMessage(field)"
                        :is-posted="isPosted(field.fieldGuid)"
                        :is-disabled="field.connectorGeneratedField"
                        :class-prefix="'table-builder-'"
                        :field-type-map="fieldTypeMap"
                        :invalid-field-map="invalidFieldMap"
                        :index="index"
                        :title="field.connectorGeneratedField ? fieldDisablingNotification : ''"
                        :is-field-changed="isFieldChanged(field)"
                        @on-type-change="setFieldType"
                        @on-field-save="updateFieldHandler(field)"
                        @field-delete="onDeleteField(field.fieldGuid, field.fieldGuid === table.postedStatusMDMFieldGuid)"
                        @on-permission-click="permissionClicked"
                        @on-add-field-between="addField(true, false, $event)"
                        @field-change="handleFieldChange"
                      />
                    </draggable>
                  </table>
                </div>
                <input-widget
                  id="postedStatus"
                  :model="postedStatus"
                  :field="fieldsSchema.postedStatus"
                  class="mt-3 ml-5"
                  :is-switch="true"
                  @change="onPostedStatusChange"
                />
                <div class="fields-header d-flex mb-3 mt-3 pr-4">
                  <b-button
                    id="addFieldButton"
                    @click="addField"
                  >
                    <font-awesome-icon :icon="['fan', 'plus']" />
                    Add Field
                  </b-button>
                </div>
              </div>
              <div
                v-else
                class="py-5 text-muted text-center"
              >
                Please provide Table Details first
              </div>
            </b-card>
          </div>

          <div
            v-if="!isNewTable && !!orchestration.data.length && !!table.entityConfig"
            class="col-sm-12 mb-3"
          >
            <workflows
              :orchestration="orchestration.data.map((i)=> ({ value: i.definitionId, text: i.displayName }))"
              :post-load-orchestration-workflow-id="table.postLoadOrchestrationWorkflowId"
              :post-delete-orchestration-workflow-id="table.postDeleteOrchestrationWorkflowId"
              :entity-config="table.entityConfig"
              :fields="[...(table?.fields ?? [])].filter(f => [...(initialData?.fields ?? [])].find(initial => initial.fieldGuid === f.fieldGuid))"
              @on-workflow-change="onTablePropertyChange"
            />
          </div>

          <div class="col-sm-12">
            <b-card no-body>
              <div
                slot="header"
                class="d-flex justify-content-between"
              >
                <span>Validation Rules</span>
                <b-button
                  id="addNewRule"
                  size="sm"
                  @click="onAddNewRuleClick"
                >
                  <font-awesome-icon :icon="['fan', 'plus']" />
                  Add Rule
                </b-button>
              </div>
              <b-card-body>
                <div class="row">
                  <div class="col-sm-12">
                    <input-widget
                      :id="`requireManualConfirmation`"
                      :model="manualConfirmationForm"
                      :field="validationRuleFields.requiresManualConfirmation"
                      :is-switch="true"
                    />
                  </div>
                </div>
                <rule-item
                  v-for="(rule, index) in validationRules"
                  :key="`validation-rules-${index}`"
                  class="mb-2"
                  :model="validationRules[index]"
                  :fields="validationRuleFields"
                  :index="index"
                  :rule="rule"
                  :table-fields="tableFields"
                  @on-delete-rule-click="onDeleteRuleClick"
                />
              </b-card-body>
            </b-card>
          </div>
        </div>
        <!-- actions -->
        <div class="mt-3">
          <b-button
            id="editTableSubmit"
            type="button"
            variant="primary"
            value="submit"
            class="mr-2"
            @click="onSubmit(true)"
          >
            <font-awesome-icon :icon="['far', 'save']" />
            Submit
          </b-button>
          <b-button
            id="editTableSaveAndClose"
            type="button"
            variant="primary"
            value="submit"
            class="mr-2"
            @click="onSubmit(false)"
          >
            <font-awesome-icon :icon="['far', 'save']" />
            Save and Close
          </b-button>
          <b-button
            type="button"
            variant="danger"
            @click="$router.go(-1)"
          >
            <font-awesome-icon :icon="['fan', 'times']" />
            Cancel
          </b-button>
        </div>
      </div>

      <designer-view v-else />
      <field-modal
        v-if="isTableFieldModal"
        :field-item="fieldsSchema"
        :table="table"
        @ok="onFieldUpdate"
        @on-config-changed="handleConfigChanged"
      />
      <field-calculation-modal
        v-if="fieldCalculation.modalSupported && fieldCalculation.modalShown"
        :field-item="fieldsSchema"
        :table="table"
      />
      <permissions-modal />
      <to-do-list-modal
        :table="table"
        :fields="fieldsSchema"
      />
    </div>
    <div v-else>
      <content-spinner :show="!disableLoading && !isLoaded()" />
    </div>
  </div>
</template>

<script>

import draggable from 'vuedraggable';
import {createNamespacedHelpers} from 'vuex';
import {isEqual, cloneDeep} from 'lodash-es';
import FieldItem from './components/FieldItem';
import FieldModal from './modals/FieldModal';
import FieldCalculationModal from './modals/FieldCalculationModal';
import {FIELD_INITAL_DATA, getFieldInitialData, SCHEMA_INITIAL_DATA} from '@/store/modules/data-management/state';
import {
  SET_TABLE_FIELDS,
  SET_TABLE_SCHEMA,
  UNSET_FOLDER_LIST,
} from '@/store/modules/data-management/mutation-types';
import InputWidget from '@/core/components/complex-table/components/InputWidget';
import {v4 as uuidv4} from 'uuid';
import ContentSpinner from '@/core/components/ContentSpinner';
import PermissionsModal from '../permissions/modals/PermissionsModal';
import Breadcrumb from '@/core/components/Breadcrumb';
import eventBus, {
  ON_ASSIGN_GROUPS_TO_FIELD_PERMISSION,
  ON_ASSIGN_USERS_TO_FIELD_PERMISSION,
} from '@/core/services/event-bus';
import NoContent from '@/core/components/NoContent';
import RuleItem from './components/RuleItem';
import Workflows from './components/Workflows';
import UsersList from '../../components/UsersList.vue';
import ToDoListModal from '@/modules/DataManagement/components/ToDoListModal.vue';
import TabsComponent from '@/modules/DataManagement/views/tableBuilder/components/TabsComponent.vue';
import DesignerView from '@/modules/DataManagement/views/designer/DesignerView.vue';
import TagsContainer from '@/modules/DataManagement/components/TagsContainer.vue';
import LookupInputComponent from '@/core/components/LookupInputComponent.vue';
/* Utilities */
import * as apiUtils from '@/core/utils/apiUtils';
import * as jsonUtils from '@/core/utils/jsonUtils';
import * as entityUtils from '@/core/utils/entityUtils';

/* State */
const { mapState: appMapState } = createNamespacedHelpers('app');
const { mapState, mapActions, mapGetters, mapMutations } = createNamespacedHelpers('dataManagement');
const { mapState: sidebarMapState, mapActions: sidebarMapActions } = createNamespacedHelpers('sidebar');
const { mapActions: designerMapActions } = createNamespacedHelpers('designer');

export default {
  name: 'TableBuilder',
  components: {
    LookupInputComponent,
    TagsContainer,
    DesignerView,
    TabsComponent,
    ToDoListModal,
    UsersList,
    RuleItem,
    NoContent,
    InputWidget,
    FieldModal,
    FieldCalculationModal,
    FieldItem,
    draggable,
    ContentSpinner,
    PermissionsModal,
    Breadcrumb,
    Workflows,
  },
  data() {
    return {
      isErrorDuringUpdateExist: {},
      tabsData: [
        {id: 1, label: 'General', value: 'general', icon: 'home'},
        {id: 2, label: 'Designer', value: 'designer', icon: 'th'},
      ],
      selectedTab: 'general',
      fieldTypeMap: {},
      invalidFieldMap: {},
      showTableDetails: true,
      fieldErrors: {},
      tablePrefix: 'dmd_',
      initialData: null,
      loaded: false,
      folder: {
        module: {},
      },
      isNewTable: false,
      table: {
        entityGuid: null,
        folderGuid: null,
        name: '',
        displayName: '',
        description: '',
        tableIcon: '',
        isActive: true,
        fields: [],
        schema: {
          folder: {},
        },
        toDoListConfig: false,
        rlsField: null,
      },
      postedStatus: {
        value: false,
      },
      errors: [],
      fieldsSchema: {
        name: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'name',
        },
        folderGuid: {
          type: 'select',
          required: true,
          label: 'Folder',
          name: 'folderGuid',
          data: {},
        },
        displayName: {
          type: 'text',
          required: true,
          label: 'Display Name',
          name: 'displayName',
        },
        description: {
          type: 'textarea',
          required: false,
          label: 'Description',
          name: 'description',
        },
        postedStatus: {
          type: 'checkbox',
          required: false,
          label: 'Posted Status',
          name: 'value',
        },
        toDoListConfig: {
          type: 'checkbox',
          required: false,
          label: 'ToDoListConfig',
          name: 'toDoListConfig',
        },
        rlsField: {
          type: 'select',
          required: false,
          label: 'Row Level Security Field',
          name: 'rlsField',
          data: {},
        },
      },
      modalField: {},
      validationRules: [],
      manualConfirmationForm: {
        requiresManualConfirmation: false,
      },
      validationRuleFields: {
        RuleName: {
          type: 'text',
          required: true,
          name: 'RuleName',
        },
        RuleDescription: {
          type: 'text',
          required: false,
          label: 'Rule Description',
          name: 'RuleDescription',
        },
        requiresManualConfirmation: {
          type: 'checkbox',
          required: false,
          label: 'Require Manual Confirmation',
          name: 'requiresManualConfirmation',
        },
        ValidationRuleType: {
          type: 'select',
          required: true,
          label: 'Rule Type',
          name: 'ValidationRuleType',
          data: {
            options: [
              {value: null, text: 'Select Rule Type', disabled: true},
              {value: 'warning', text: 'Warning'},
              {value: 'error', text: 'Error'},
              {value: 'dataQuality', text: 'Data Quality'},
            ],
          },
        },
        LogicalConjunction: {
          type: 'toggle-switch',
          name: 'LogicalConjunction',
        },
        defaultFieldType: 'fieldGuid',
        LeftHandSide: {
          ExpressionItemType: {
            type: 'select',
            required: false,
            name: 'ExpressionItemType',
            data: {
              options: [
                {value: null, text: 'Select Expression Item Type', disabled: true},
                {value: 'fieldGuid', text: 'Field'},
                {value: 'literal', text: 'Value'},
                {value: 'literalList', text: 'List'},
              ],
            },
          },
        },
        RightHandSide: {
          ExpressionItemType: {
            type: 'select',
            required: false,
            name: 'ExpressionItemType',
            data: {
              options: [
                {value: null, text: 'Select Expression Item Type', disabled: true},
                {value: 'fieldGuid', text: 'Field'},
                {value: 'literal', text: 'Value'},
                {value: 'literalList', text: 'List'},
              ],
            },
          },
        },
        operation: {
          type: 'select',
          required: false,
          name: 'operation',
          data: {
            options: [
              {value: null, text: 'Select Operation', disabled: true, fieldTypes: ['*']},
              {value: 'equal', text: 'Equal', fieldTypes: ['*']},
              {value: 'notEqual', text: 'Not Equal', fieldTypes: ['*']},
              {
                value: 'lessThan',
                text: 'Less Than',
                fieldTypes: ['wholeNumber', 'decimalNumber', 'floatNumber', 'date', 'dateTime'],
              },
              {
                value: 'lessThanOrEqualTo',
                text: 'Less Than Or Equal To',
                fieldTypes: ['wholeNumber', 'decimalNumber', 'floatNumber', 'date', 'dateTime'],
              },
              {
                value: 'greaterThan',
                text: 'Greater Than',
                fieldTypes: ['wholeNumber', 'decimalNumber', 'floatNumber', 'date', 'dateTime'],
              },
              {
                value: 'greaterThanOrEqualTo',
                text: 'Greater Than Or Equal To',
                fieldTypes: ['wholeNumber', 'decimalNumber', 'floatNumber', 'date', 'dateTime'],
              },
              {value: 'isInList', text: 'Is In List', fieldTypes: ['*']},
              {value: 'isNotInList', text: 'Is Not In List', fieldTypes: ['*']},
            ],
          },
        },
        nonRightHandSideOperations: [
          {
            value: 'isNull',
            text: 'Is Null',
            fieldTypes: ['text', 'wholeNumber', 'decimalNumber', 'floatNumber', 'dateTime', 'date', 'lookup'],
          },
          {
            value: 'isNotNull',
            text: 'Is Not Null',
            fieldTypes: ['text', 'wholeNumber', 'decimalNumber', 'floatNumber', 'dateTime', 'date', 'lookup'],
          },
          {value: 'isBlank', text: 'Is Blank', fieldTypes: ['text']},
          {value: 'isNotBlank', text: 'Is Not Blank', fieldTypes: ['text']},
          {value: 'isNullOrBlank', text: 'Is Null Or Blank', fieldTypes: ['text']},
          {value: 'isNotNullOrBlank', text: 'Is Not Null Or Blank', fieldTypes: ['text']},
          {value: 'isNumeric', text: 'Is Numeric', fieldTypes: ['text']},
          {value: 'isDate', text: 'Is Date', fieldTypes: ['text']},
        ],
      },
      changedEntityGuids: [],
      /** Field Calculation control data. */
      fieldCalculation: {
        modalSupported: mapGetters['fieldCalculationModalSupported'],
        modalShown: false,
        field: {},
      },
      /** Component Control data. */
      fieldsLoading: false,
      disableLoading: false,
      tableUpdateTimeout: null,
      fieldDisablingNotification: 'This field  is automatically generated by a system connector. Editing its properties is restricted to maintain data integrity.',
    };
  },
  watch: {
    ['clonedItems']: {
      handler(newValue) {
        if (newValue?.fields?.length && this.initialData?.fields) {
          this.changedEntityGuids = this.getChangedFields();
        }
      },
      deep: true,
      immediate: true,
    },
    ['initialData']: {
      handler() {
        if (this.table?.fields?.length && this.initialData?.fields) {
          this.changedEntityGuids = this.getChangedFields();
        }
      },
      deep: true,
      immediate: false,
    },
    ['ini']: {
      handler() {
        if (this.table?.fields?.length && this.initialData?.fields) {
          this.changedEntityGuids = this.getChangedFields();
        }
      },
      deep: true,
      immediate: false,
    },
    ['isNewTable']: {
      handler(newValue, oldValue) {
        if (oldValue === true && newValue === false) {
          this.$router.push({
            path: `/app/all-modules/${this.table.moduleGuid}/builder/${this.table.entityGuid}?moduleUuid=${this.table.moduleGuid}`,
          });
        }
      },
      immediate: true,
    },
    ['folders.data']() {
      this.fieldsSchema.folderGuid.data = {
        options: this.folders.data.map(f => ({text: f.folderName, value: f.folderGuid})),
      };
    },
    ['rlsField.data']() {
      this.fieldsSchema.rlsField.data = {
        options: this.rlsFieldOptions,
      };
    },
    /* Track changes on: menuItem -> sidebar.menuItems. */
    menuItems: {
      handler(val) {
        /* Mar-2025 AH Added ? to 'currentFolder?.children' to avoid console errors when localStorage.getItem('folder') not populated. */
        if (!localStorage.getItem('folder')) return;
        const currentModule = val.find(el => el.moduleGuid === this.$route.params.moduleUuid);
        const currentFolder = currentModule.children.find(el => el.folderGuid === localStorage.getItem('folder'));
        const currentTable = currentFolder?.children.find(el => el.entityGuid === this.$route.params.tableUuid);
        this.setSelectedModuleName(currentModule.label);
        this.setSelectedTableAction(currentTable);
      },
    },
    rlsFieldOptions: {
      immediate: true,
      handler(newVal) {
        // Add the "No RLS Field" option and map over new options
        this.fieldsSchema.rlsField.data = {
          options: [
            { text: '--- No RLS Field Selected ---', value: null },  // Add "No RLS Field" as first option
            ...newVal,
          ],
        };
      },
    },
  },
  computed: {
    ...appMapState({
      isFourthVersion: state => state.isFourthVersion,
    }),
     ...mapState(['folders', 'fieldTypes', 'onlyIcons']),
    ...mapState({
      loading: state => state.builder.loading,
      schema: state => state.builder.schema,
      fieldValues: state => state.fieldValues,
      convertMap: state => state.convertMap,
      tables: state => state.tables,
      builder: state => state.builder,
      permissionItemTypes: state => state.permissionItemTypes,
      folderData: state => state.folderData.data,
      tableData: state => state.table,
      permissionTypes: state => state.permissionTypes,
      orchestration: state => state.orchestration,
      isTableFieldModal: state => state.builder.tableFieldModal.show,
    }),
    ...sidebarMapState({
      menuItems: state => state.menuItems,
      selectedModuleName: state => state.selectedModuleName,
      selectedModule: state => state.selectedModule,
      selectedFolderName: state => state.selectedFolderName,
      tablesList: state => state.tablesList,
    }),
    ...mapGetters(['fieldTypes','fieldCalculationModalSupported']),
    tableFields() {
      return this.tableData.schema.fields.filter(f => {
        if ([this.permissionTypes.none, this.permissionTypes.deny].includes(f.currentUserPermissionLevel)) {
          return false;
        }

        let show = f.displayInTable && !f.isPrimaryKey;

        if (!this.readOnly) {
          show = show && !f.isInternal;
        }

        return show;
      });
    },
    rlsFieldOptions() {
      return this.tableData.schema.fields
        .filter(f => {
          // Example filtering logic based on permission and other fields
          if ([this.permissionTypes.none, this.permissionTypes.deny].includes(f.currentUserPermissionLevel)) {
            return false;
          }

          let show = f.displayInTable && !f.isPrimaryKey;

          if (!this.readOnly) {
            show = show && !f.isInternal;
          }

          if(f.dataTypeName !== 'text') {
            show = false;
          }

          return show;
        })
        .map(f => ({
          text: f.displayName,
          value: f.fieldGuid,
        }));
    },
    isPosted() {
      return (fieldGuid) => {
        return this.table.postedStatusMDMFieldGuid === fieldGuid;
      };
    },
    clonedItems() {
      return JSON.parse(JSON.stringify(this.table));
    },
    allowFieldCalculationModal(){
      return isFouthVersion();
    },
  },
  provide() {
    return {
      /**
       * Method: Open Field Calculation modal.
       * - used by FieldItem component on icon click.
        * @param field 
       */
      showFieldCalculationModal:(field) => { this.showFieldCalculationModal(field); },
      /**
       * Method: Close Field Calculation modal.
       * - used by Field Calculation modal.
       */
      hideFieldCalculationModal:() => { this.hideFieldCalculationModal(); },
       /**
        * Store the Field's definition in the backend.
        * - Used by Calculation Modal to send updated Calculation to the backend and refresh the field definition in memory.
        * @param field 
        * @returns True if Field updated successfully.
        */
      storeAndRefreshField: this.storeAndRefreshField,
      /** Data and functionality used by Field Calculation Modal component. */
      fieldCalculationData: this.fieldCalculation,
    };
  },
  methods: {
    ...mapActions([
      'getTableSchema',
      'updateFieldPosition',
      'getFolders',
      'addTable',
      'updateTable',
      'resetTableData',
      'updateEntityConfig',
      'getTables',
      'getFolderData',
      'restoreTable',
      'showPermissionsModal',
      'getFields',
      'getTable',
      'getTableFields',
      'savePostedStatusField',
      'showTodoListModal',
      'hideTodoListModal',
      'showFieldBuilderModal',
      'saveFieldsOrder',
      'deleteTable',
      'updateFields',
      'deleteField',
      'deletePostedStatusField',
      'getOrchestration',
      'updateTableFieldsIsCalculationState',
      'updateFieldItemCalculationLoadingState',
    ]),
    ...sidebarMapActions([
      'getModuleDetails',
      'getSidebarStructure',
      'setSelectedModuleName',
      'setSelectedTableAction',
    ]),
    ...designerMapActions([
      'updateUnrelatedFields',
      'removePostedStatusFromRelatedFields',
    ]),
    ...mapMutations([SET_TABLE_SCHEMA, SET_TABLE_FIELDS, UNSET_FOLDER_LIST]),
    isFieldChanged(field) {
      return [...this.changedEntityGuids].includes(field.fieldGuid);
    },
    getSelectedTab(tab) {
      this.selectedTab = tab;
    },
    getChangedFields() {
      return this.table.fields.reduce((acc, f) => {
        if (f.fieldGuid === this.table.postedStatusMDMFieldGuid) {
          return acc;
        }

        const initialField = this.initialData.fields.find(initial => initial.fieldGuid === f.fieldGuid);

        if (!initialField) {
          acc.push(f.fieldGuid);
          return acc;
        }

        const {ordinalPosition, ...currentFieldState} = f;
        const {ordinalPosition: _, ...originalFieldState} = initialField;

        if (!isEqual(currentFieldState, originalFieldState)) {
          acc.push(f.fieldGuid);
        }

        return acc;
      }, []);
    },

    permissionClicked(event) {
      this.showPermissionsModal(event);
    },

    /** Open the Field Calculation Modal popup. */
    showFieldCalculationModal(field) {
      /* Clone the field for use by Calculation components to avoid modifying original. */
      this.fieldCalculation.field = JSON.parse(JSON.stringify(field || {}));
      this.fieldCalculation.modalShown = true;
    },

    /** Close the Field Calculation Modal popup. */
    hideFieldCalculationModal(field) {
      /* Clear the Field Calculation data. */
      this.fieldCalculation.field = {};
      this.fieldCalculation.modalShown = false;
    },

    addField(
      setFocus = true,
      isPostedStatus = false,
      enterFieldIndex = null,
      displayName = '',
      fieldGuid = null,
    ) {
      const field = getFieldInitialData();
      if (isPostedStatus) {
        field.isRequired = false;
      }
      field.fieldGuid = fieldGuid || uuidv4();
      field.entityGuid = this.table.entityGuid;
      field.displayName = displayName;
      field.columnAlignment = null;
      field.dataTypeJSON = {
        MinimumCharacters: 0,
        MaximumCharacters: -1,
      };
      field.isBusinessKey = false;
      field.isNew = true;

      const fieldIndex = enterFieldIndex !== null
        ? enterFieldIndex
        : (this.table?.fields?.length
          ? this.table.fields.length - 1
          : 0
        );

      this.table?.fields?.splice(fieldIndex + 1, 0, field);

      if (setFocus) {
        this.$nextTick(() => {
          const $newField = document.getElementById(`field-item-${fieldIndex + 1}`);
          if($newField) {
            const $input = $newField.querySelector('input');
            $input.focus();
          }
        });
      }
      return field;
    },

    async onPostedStatusChange(value) {
      if (value) {
        const res = await this.savePostedStatusField({
          entityGuid: this.table.entityGuid,
          params: {postedStatusField: {displayName: 'Posted Status'}},
        });

        // this[SET_TABLE_FIELDS](this.table.fields)
        if (res.success) {
          const postedField = this.addField(false, true, null, 'Posted Status', res.body);
          await this.updateUnrelatedFields({mode: 'add', data: postedField});
          this.table.postedStatusMDMFieldGuid = res.body;
          this.$toasted.success('Posted Status Field was successfully created', {
            duration: TOASTER_DURATION,
          });
        } else {
          this.postedStatus.value = false;
          this.$toasted.error(res.body.ErrorMessage || 'Unable to create Posted Status field', {
            duration: TOASTER_DURATION,
          });
        }
      } else {
        await this.onDeleteField(this.table.postedStatusMDMFieldGuid, true);
        await this.removePostedStatusFromRelatedFields();
        await this.updateUnrelatedFields({mode: 'delete', data: null});
      }
    },

    async onDisplayNameChange() {
      if (!this.isNewTable) return;
      try {
        const tableDataResponse = await this.onUpdateTable(true);

        if(!tableDataResponse) return;
        this.table.moduleGuid = tableDataResponse.moduleGuid;
        if (this.isNewTable) {
          this.isNewTable = false;
        }
        this.$toasted.success(
          `Table "${tableDataResponse.displayName}" was saved`,
          {duration: TOASTER_DURATION},
        );
      } catch (error) {
        this.$toasted.error(error, {duration: TOASTER_DURATION});
      }
    },

    async onFieldUpdate(field) {
      const index = this.table.fields.findIndex(f => f.fieldGuid === field.fieldGuid);
      if (index > -1) {
        this.table.fields[index] = field;
        this.table.fields = [...this.table.fields];
      }
    },

    async onDeleteField(fieldGuid, withPostedStatus = false) {
      const field = this.table.fields.find(f => f.fieldGuid === fieldGuid);
      try {
        const confirmed = await this.$confirm('Deleting field...', `You are deleting ${field?.displayName ?? ''} field. Are you sure you want to continue?`);
        if (!confirmed) {
          return;
        }
        // If new field has not yet been saved, only delete from front end 
        if (field.isNew) {
          this.table.fields = this.table.fields.filter(f => fieldGuid !== f.fieldGuid);
          return;
        }
        if (withPostedStatus) {
          const response = await this.deletePostedStatusField({entityGuid: this.table.entityGuid, field});
          if(!!response.success){
            this.table.fields = this.table.fields.filter(f => fieldGuid !== f.fieldGuid);
            this.table.postedStatusMDMFieldGuid = '00000000-0000-0000-0000-000000000000';
            this.postedStatus.value = false;
          }else{
            this.$toasted.error(response.body.ErrorMessage, {
              duration: TOASTER_DURATION,
            });
          }
        } else {
          this.table.fieldsLoading = true;
          const response = await this.deleteField(field);

          if(!!response.success){
            this.table.fields = this.table.fields.filter(f => fieldGuid !== f.fieldGuid);  
            if (!this.initialData.fields.find(f => f.fieldGuid === fieldGuid)) {
              this.table.fieldsLoading = false;
              return;
            }
            await this.updateUnrelatedFields({mode: 'delete', data: field});
          }else{
            this.$toasted.error(response.body.ErrorMessage, {
              duration: TOASTER_DURATION,
            });
          }

        }

        this[SET_TABLE_FIELDS](this.table.fields);
        this.fieldsLoading = false;
      } catch (error) {
        this.fieldsLoading = false;
        this.$toasted.error(`An error occurred during field deletion: ${error}`, {
          duration: TOASTER_DURATION,
        });
      }
    },

    /**
     * Used by FieldItem to enable 'Save' icon and display message if disabled.
     * - Conditions: Missing Field label and missing Lookup Related Field.
     * @param {Object} fld Field definition. 
     */
    checkCanSaveField(fld) {
      return !this.saveFieldProhibitedMessage(fld);
    },
    /**
     * Determine the reason/message why the Field cannot be saved.
     * @param fld Field definition.
     * @returns Message if Field cannot be saved, otherwise null.
     */
    saveFieldProhibitedMessage(fld) {
      if (fld.displayName === '') {
        return 'Please enter a Field Label first.';
      }
      if (fld.dataTypeName === this.fieldValues.lookup && !fld.dataTypeJSON.FieldToDisplayGuid)
        return 'Please select a Related Table Column in the settings first.';
      return null;
    },

    /**
     * Used by FieldItem to enable 'Delete' icon and display message if disabled.
     * - Only condition appears to be Field in use in Workflow.
     * - Also to disable renaming Field.
     * @param {Object} fld Field definition. 
     */
     checkCanDeleteField(fld) {
      return !this.deleteFieldProhibitedMessage(fld);
    },
    /**
     * Determine the reason/message why the Field cannot be deleted.
     * @param fld Field definition.
     * @returns Message if Field cannot be deleted, otherwise null.
     */
     deleteFieldProhibitedMessage(fld) {
      if (this.table.entityConfig.MidLoadWorkflow?.Columns && this.table.entityConfig.MidLoadWorkflow?.Columns.includes(fld.name)) {
        return 'Field cannot be deleted, it is currently in use in a workflow.';
      }
      return null;
    },

    fieldsValidation(fields) {
      return fields.reduce((isValid, currentField) => {
        if (!isValid) return false;

        if (currentField.displayName === '') {
          this.$toasted.error('Field does not have label', {
            duration: TOASTER_DURATION,
          });
          return false;
        }

        const json = typeof currentField.dataTypeJSON === 'string'
          ? JSON.parse(currentField.dataTypeJSON)
          : currentField.dataTypeJSON;

        if (currentField.dataTypeName === this.fieldValues.lookup && !json.FieldToDisplayGuid) {
          this.$toasted.error(`Field ${currentField.displayName} does not have related table's parameters`, {
            duration: TOASTER_DURATION,
          });
          return false;
        }

        if (currentField.dataTypeName === this.fieldValues.parentChildHierarchy && (!json.FieldToDisplayGuid || !json.LevelFieldGuid)) {
          this.$toasted.error(`Field ${currentField.displayName} does not have related table's parameters`, {
            duration: TOASTER_DURATION,
          });
          return false;
        }

        if (json.MaximumLevelSelectable !== null && json.MaximumLevelSelectable < json.MinimumLevelSelectable) {
          this.$toasted.error('Maximum level must be greater than minimum', {
            duration: TOASTER_DURATION,
          });
          return false;
        }

        return true;
      }, true);
    },

    rulesValidation() {
      const ruleHasError = this.validationRules.some(rule => {
        if (rule.ValidationRuleType === null || rule.ValidationRuleType === undefined) {
          this.$toasted.error('A rule type must be selected', {
            duration: TOASTER_DURATION,
          });
          return true;
        }

        if (!this.hasExpression(rule.LogicTree)) {
          this.$toasted.error('Rule must have one or more expressions', {
            duration: TOASTER_DURATION,
          });
          return true;
        }

        const expressionHasError = rule.LogicTree.Expressions.some(expression => {
          const [success, message] = this.isValidExpression(expression);
          if (!success) {
            this.$toasted.error(message || 'Rule is not valid', {
              duration: TOASTER_DURATION,
            });
            return true;
          }
          return false;
        });

        return expressionHasError;
      });
      return !ruleHasError;
    },

    tableDetailsValidation() {
      if (this.$route.params.tableUuid && this.tableData.data.length > 0) {
        for (const currentField of this.table.fields) {
          const field = this.initialData.fields.find((f) => f.fieldGuid === currentField.fieldGuid);
          if (field) {
            if (!field.isRequired && currentField.isRequired) {
              this.$toasted.error(`Cannot change field '${currentField.displayName}' to required. Entity has existing records`, {
                duration: TOASTER_DURATION,
              });
              return false;
            }
          } else if (currentField.isRequired) {
            this.$toasted.error(`Cannot create required field '${currentField.displayName}'. Entity has existing records`, {
              duration: TOASTER_DURATION,
            });
            return false;
          }
        }
      }
      return true;
    },

    validateForm() {
      return this.fieldsValidation(this.table.fields)
          && this.rulesValidation()
          && this.tableDetailsValidation()
          && this.table.displayName.length;
    },

    hasExpression(item) {
      if (item.Expressions.length > 0) {
        return true;
      } else {
        if (item.ChildLogicTrees.length > 0) {
          let hasExpression = true;
          for (const index in item.ChildLogicTrees) {
            hasExpression = this.hasExpression(item.ChildLogicTrees[index]);
            if (hasExpression) {
              return true;
            }
          }
          return hasExpression;
        } else {
          return false;
        }
      }
    },

    isValidExpression(exp) {
      if (exp) {
        if (!exp.LeftHandSide || exp.LeftHandSide.value === '' || exp.LeftHandSide.value === null) {
          return [false, 'Entity field is required on left hand side'];
        }
        else if (!exp.operation) {
          return [false, 'An operation is required'];
        }
        else if (this.operatorRequiresRightHandSide(exp.operation) && (!exp.RightHandSide || exp.RightHandSide.ExpressionItemType === '' || exp.RightHandSide.ExpressionItemType === null)) {
          return [false, 'Operator requires right hand side expression item type'];
        }
        else if (this.operatorRequiresRightHandSide(exp.operation) && (!exp.RightHandSide || exp.RightHandSide.value === '' || exp.RightHandSide.value === null || (Array.isArray(exp.RightHandSide.value) && !exp.RightHandSide.value.length))) {
          return [false, 'Operator requires a right hand side value'];
        }
      }
      return [true];
    },

    isJSON(str) {
      try {
        JSON.parse(str);
      } catch (e) {
        return false;
      }
      return true;
    },

    getFormattedValidationRulesData({type}) {
      const escapeExpressionValue = ({logicTree, type}) => {
        logicTree.Expressions.map(expression => {
          const rightHandSide = expression.RightHandSide;
          if (rightHandSide && rightHandSide.ExpressionItemType === 'literal') {

            const field = this.tableFields.find(f => f.fieldGuid === expression.LeftHandSide.value);
            if (rightHandSide.value && field && this.isFieldNumber(field)) {
              rightHandSide.value = parseFloat(rightHandSide.value);
            } else if (type === 'escape') {
              // rightHandSide.value = JSON.stringify(rightHandSide.value)
            } else if (this.isJSON(rightHandSide.value)) {
              rightHandSide.value = JSON.parse(rightHandSide.value);
            }
          }
          if (this.operatorRequiresRightHandSide(expression.operation)) {
            // Show Error validation rule requires right hand side operator
          } else if (type === 'escape') {
            if (expression.RightHandSide && !expression.RightHandSide.value) {
              expression.RightHandSide = null;
            }
          } else if (type === 'unescape') {
            if (!expression.RightHandSide) {
              expression.RightHandSide = {ExpressionItemType: 'fieldGuid', value: null};
            }
          }

          return expression;
        });

        const childLogicTrees = logicTree.ChildLogicTrees;
        if (childLogicTrees.length) {
          childLogicTrees.map(childLogicTree => {
            escapeExpressionValue({logicTree: childLogicTree, type});
            return childLogicTree;
          });
        }
      };

      if (this.validationRules[0]) {
        this.validationRules.map(rule => {
          const logicTree = rule.LogicTree;
          escapeExpressionValue({logicTree, type});
          return rule;
        });
      }

    },

    isFieldNumber(field) {
      const type = field.fieldType || field.dataTypeName;
      return type === 'decimalNumber' || type === 'wholeNumber' || type === 'floatNumber';
    },

    async submitTableData(data, entityCore = false) {
      if (this.$route.params.tableUuid) {
        const resp = await this.updateTable({data, entityCore});
        await this.getSidebarStructure();
        await this.getModuleDetails(data.moduleGuid);
        return resp;
        // this.$router.push({name: 'dataManagementModuleView', params: {moduleUuid: this.$route.params.moduleUuid}});
        // this.$router.push(`/items/${itemId}`, {});
      }
      const resp = await this.addTable(data);
      await this.getSidebarStructure();
      await this.getModuleDetails(data.moduleGuid);

      return resp;
    },

    async onUpdateTable(entityCore = false, customPayload = undefined) {

      try {
        if (!this.validateForm()) {
          throw new Error('Validation failed');
        }
        const {fields, ...tableData} = this.table;
        const payload = {
          ...tableData,
          entityConfig: {
            ...tableData.entityConfig,
            PostedStatusMDMFieldGuid: tableData.postedStatusMDMFieldGuid !== '00000000-0000-0000-0000-000000000000' ? tableData.postedStatusMDMFieldGuid : null,
          },
          postedStatusMDMFieldGuid: !!tableData.postedStatusMDMFieldGuid
            ? tableData.postedStatusMDMFieldGuid
            : '00000000-0000-0000-0000-000000000000',
          moduleGuid: this.$route.params.moduleUuid,
          isActive: true,
          currentUserPermissionLevel: this.permissionTypes.admin,
          toDoListConfig: this.builder.schema.toDoListConfig,
          validationRules: {
            ValidationRules: [...this.validationRules],
            RequiresManualConfirmation: this.manualConfirmationForm.requiresManualConfirmation,
          },
        };

        return await this.submitTableData(customPayload ?? payload, entityCore);
      } catch (error) {
        throw error;
      }
    },

    async onUpdateFields(fields, isSubmit = false) {
      try {
        if (this.isNewTable || fields.length === 0) {
          return;
        }

        if (!this.fieldsValidation(fields)) {
          return;
        }

        const fieldsArray = fields
          .filter(fld => fld.fieldGuid !== this.table.postedStatusMDMFieldGuid)
          .map(fld => {
            const currentField = fld;
            // dataTypeJSON must be serialised if not already a string.
            return (/string/i.test(typeof currentField.dataTypeJSON))
              ? currentField
              : {
                ...currentField,
                dataTypeJSON: JSON.stringify(currentField.dataTypeJSON),
              };
          }); 
        const responseData = await this.updateFields(fieldsArray);
        const isErrorDuringTheRecordsSavingExist = {
          isError: !!responseData['item2'].length,
          field: responseData['item2'][0],
        };

        isErrorDuringTheRecordsSavingExist.isError
          ? this.$toasted.error(isErrorDuringTheRecordsSavingExist.field.errorMessage, {duration: TOASTER_DURATION * 2})
          : !isSubmit
            ? this.$toasted.success('Fields successfully updated.', {duration: TOASTER_DURATION})
            : null;

        const newData = new Map(responseData['item1'].map(f => {
          return [f.fieldGuid, {
            ...f,
            dataTypeJSON: JSON.parse(f.dataTypeJSON),
          }];
        }));  
        const initialData = new Map(this.initialData.fields.map(f => [f.fieldGuid, f]));
        const currentData = new Map(this.table.fields.map(f => [f.fieldGuid, f]));

        const resultArray = [...this.initialData.fields];
        const currentArray = [...this.table.fields];

        newData.forEach((newVal, newKey) => {
          if (initialData.has(newKey)) {
            const index = resultArray.findIndex(item => item.fieldGuid === newKey);
            resultArray[index] = {...newVal};
          } else {
            resultArray.push(newVal);
          }
        });

        newData.forEach((newVal, newKey) => {
          if (currentData.has(newKey)) {
            const index = currentArray.findIndex(item => item.fieldGuid === newKey);
            currentArray[index] = {...newVal};
          } else {
            currentArray.push(newVal);
          }
        });

        this.initialData.fields = resultArray;
        this.table.fields = currentArray;
        await this.updateOrderPosition(true);

        return isErrorDuringTheRecordsSavingExist;

      } catch (error) {
        this.$toasted.error(error.message, {
          duration: TOASTER_DURATION,
        });
      } finally {
        this.fieldsLoading = false;
      }
    },

    /**
     * Sumbit the Field definition to the backend and refresh the definition in memory.
     * - Used by Calculation Modal to send updated Calculation to the backend and refresh the field definition in memory.
     * @param field definition object
     * @param isSubmit flag (true exclude notification messages)
     */
    async storeAndRefreshField(field, isSubmit = false)
    {
      if (await this.updateFieldHandler(field, isSubmit))
      {
        /* Don't seem to need to do the onFieldUpdate(). */
        /*await this.onFieldUpdate(field);*/
        return true;
      }
    },

    async updateFieldHandler(field, isSubmit = false) {
      const targetField = field.fieldGuid;
      this.updateFieldItemCalculationLoadingState(targetField);

      await this.updateUnrelatedFields({mode: 'add', data: field});
      const config = field.dataTypeJSON.FuzzyMatchConfig;
      if(field.dataTypeName === 'lookup' && config) {
        field.dataTypeJSON.FuzzyMatchConfig = {
          ...field.dataTypeJSON.FuzzyMatchConfig,
          AutoMatchThreshold: !config.AutoMatchThreshold ? null : +config.AutoMatchThreshold / 100,
          MatchThreshold: +config.MatchThreshold / 100,
          RecalculateWhenSourceDataChanges: config.RecalculateWhenSourceDataChanges,
          FieldConfigs: config.FieldConfigs.map(el => {
            delete el.key;
            return {
              ...el,
              MatchThreshold: +el.MatchThreshold / 100,
            };
          }),
        };
      }
      let isErrorDuringTheRecordsSavingExist = await this.onUpdateFields([field], isSubmit);
      isErrorDuringTheRecordsSavingExist = {...cloneDeep(isErrorDuringTheRecordsSavingExist), targetField};
      this.isErrorDuringUpdateExist = isErrorDuringTheRecordsSavingExist || {isError: false};
      return !this.isErrorDuringUpdateExist?.isError;
    },

    async onSubmit(isSubmit) {
      try {
        const tableDataResponse = await this.onUpdateTable();
        const changedFields = this.table.fields.filter(f => this.changedEntityGuids.includes(f.fieldGuid));
        await this.updateUnrelatedFields({mode: 'add', data: changedFields});
        const elementsAfterTransform = changedFields.map(el => {
          const config = el.dataTypeJSON.FuzzyMatchConfig;
          if(el.dataTypeName === 'lookup' && config) {
            el.dataTypeJSON.FuzzyMatchConfig = {
              ... el.dataTypeJSON.FuzzyMatchConfig,
              AutoMatchThreshold: +config.AutoMatchThreshold / 100,
              MatchThreshold: +config.MatchThreshold / 100,
              RecalculateWhenSourceDataChanges: config.RecalculateWhenSourceDataChanges,
              FieldConfigs: config.FieldConfigs.map(el => ({
                ...el,
                MatchThreshold: +el.MatchThreshold / 100,
              })),
            };
            return el;
          }
          return el;
        });
        const isErrorDuringTheRecordsSavingExist = await this.onUpdateFields(elementsAfterTransform, isSubmit);
        this.isErrorDuringUpdateExist = isErrorDuringTheRecordsSavingExist || {isError: false};
        if (isSubmit) {
          if (this.isNewTable) {
            this.$router.push({
              path: `/app/all-modules/${tableDataResponse.moduleGuid}`,
            });
            this.isNewTable = false;
          }
          if (!this.isErrorDuringUpdateExist.isError) {
            this.$toasted.success(
              `Table "${tableDataResponse.displayName}" was saved`,
              {duration: TOASTER_DURATION},
            );
            this.initialData = {...cloneDeep(this.table)};
          }

        } else {
          this.$router.push(`/app/all-modules/${tableDataResponse.moduleGuid}/table/${this.table.entityGuid}?moduleUuid=${tableDataResponse.moduleGuid}`);
        }
      } catch (e) {
        this.$toasted.error(e, {duration: TOASTER_DURATION});
      }
    },

    isLoaded() {
      return this.folders.loaded && !this.builder.loading && !!this.orchestration.loaded;
    },

    setFieldType(params) {
      const updatedVal = {};
      updatedVal[params.uuid] = params.type;
      this.fieldTypeMap = {...this.fieldTypeMap, ...updatedVal};
    },

    setFieldTypeMap() {
      return;
      this.fieldTypeMap = {};
      this.table.fields.forEach((field) => {
        this.setFieldType({uuid: field.uuid, type: field.type});
      });
    },

    canEdit(schema) {
      return ['write'].includes(schema.currentUserPermission.access_level);
    },

    async deleteTableHandler(table) {
      const success = await this.$confirm('Deleting Table...', `You are deleting Table ${table.displayName}. Are you sure you want to continue?`);
      if (success) {
        const {success} = await this.deleteTable(table);
        if (success) {
          this.$toasted.success(`Table "${table.displayName}" was deleted`, {
            duration: TOASTER_DURATION,
          });
          this.$router.push(`/app/all-modules/${this.table.moduleGuid}`);
        }
      }
    },

    addUserPermissionToField(event) {
      const fieldIndex = this.table.fields.findIndex(field => field.uuid === event.fieldUuid);
      if (fieldIndex > -1) {
        if (!this.table.fields[fieldIndex].permissions) {
          this.table.fields[fieldIndex].permissions = [];
        }
        /*
        Remove existing permissions. For example if read permission is changed to write for a single user,
         then remove old permission
        */
        for (const permission of event.permissions) {
          const existingPermissionIndex = this.table.fields[fieldIndex].permissions.findIndex(perm => perm.user_uuid === permission.user_uuid);
          if (existingPermissionIndex > -1) {
            this.table.fields[fieldIndex].permissions.splice(existingPermissionIndex, 1);
          }
        }
        this.table.fields[fieldIndex].permissions = [
          ...this.table.fields[fieldIndex].permissions,
          ...event.permissions,
        ];
      }
    },

    addGroupPermissionToField(event) {
      const fieldIndex = this.table.fields.findIndex(field => field.uuid === event.fieldUuid);
      if (fieldIndex > -1) {
        if (!this.table.fields[fieldIndex].permissions) {
          this.table.fields[fieldIndex].permissions = [];
        }
        /*
        Remove existing permissions. For example if read permission is changed to write for a single group,
         then remove old permission
        */
        for (const permission of event.permissions) {
          const existingPermissionIndex = this.table.fields[fieldIndex].permissions.findIndex(perm => perm.group_uuid === permission.group_uuid);
          if (existingPermissionIndex > -1) {
            this.table.fields[fieldIndex].permissions.splice(existingPermissionIndex, 1);
          }
        }
        this.table.fields[fieldIndex].permissions = [
          ...this.table.fields[fieldIndex].permissions,
          ...event.permissions,
        ];
      }
    },

    getTableData() {
      const params = {
        id: this.$route.params.tableUuid,
        pageNumber: 1,
        pageSize: 0,
      };
      this.getTable(params);
    },

    eventListeners() {
      eventBus.$on(ON_ASSIGN_USERS_TO_FIELD_PERMISSION, this.addUserPermissionToField);
      eventBus.$on(ON_ASSIGN_GROUPS_TO_FIELD_PERMISSION, this.addGroupPermissionToField);
    },

    onAddNewRuleClick() {
      this.validationRules.push({
        RuleName: 'New Rule',
        RuleDescription: '',
        ValidationRuleType: null,
        LogicTree: {
          LogicalConjunction: null,
          ChildLogicTrees: [],
          Expressions: [],
        },
      });
    },

    onDeleteRuleClick(index) {
      this.validationRules.splice(index, 1);
    },

    async updateOrderPosition(isUpdateFieldsRequest = false) {
      const fieldsWithNewOrder = [...this.table.fields].reduce((acc, field, idx) => {
        if (!field.isNew) {
          acc.push({fieldGuid: field.fieldGuid, ordinalPosition: idx});
        }
        return acc;
      }, []);
      if (fieldsWithNewOrder.length > 0) {
        const isSuccess = await this.saveFieldsOrder(fieldsWithNewOrder);
        !isUpdateFieldsRequest && this.$toasted[isSuccess ? 'success' : 'error'](
          isSuccess
            ? 'Fields have been successfully reordered'
            : 'An error occurred while saving process, please try again',
          {duration: TOASTER_DURATION},
        );
      }
    },

    async onDragHandler() {
      await this.updateOrderPosition();
    },

    operatorRequiresRightHandSide(op) {
      return !this.validationRuleFields.nonRightHandSideOperations.find((val) => val.value === op);
    },

    openTodoListModal() {
      const todoListConfigData = this.table.toDoListConfigJSON;
      this.showTodoListModal(todoListConfigData);
    },

    handleConfigChanged(field) {
      this.table.fields = this.table.fields.map(f => {
        if (f.fieldGuid === field.fieldGuid) {
          return field;
        }
        return f;
      });
    },

    onTypeChange(f, newDataTypeName) {
      f.dataTypeName = newDataTypeName;
      entityUtils.resetDataTypeProperties(f);
      if (!f.calculation)
        this.updateTableFieldsIsCalculationState(false);
      return f;
    },

    onTablePropertyChange({propertyName, value}) {
      this.setValueByPath(this.table, propertyName, value);
    },

    handleFieldChange({fieldGuid, fieldName, newValue}) {
      this.table.fields = this.table.fields.map(f => {
        if (f.fieldGuid === fieldGuid) {
          if (fieldName === 'dataTypeName') {
            f = this.onTypeChange(f, newValue);
          } else {
            f[fieldName] = newValue;
          }
        }
        return f;
      });
    },

    setValueByPath(obj, path, value) {
      const keys = path.split('.');
      let current = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;
    },
  },
  created() {
    this.fieldCalculation.modalSupported = this.fieldCalculationModalSupported;
  },
  async mounted() {

    /* Load all Field definitions to state. */
    await this.getFields();

    this.table.rlsField = this.tableData?.schema?.rlsField || null;
    this.table.folderGuid = this.$route.query.folderUuid;
    if (this.$route.params.tableUuid) { //Edit existing table
      this.showTableDetails = false;
      await this.getTableSchema(this.$route.params.tableUuid);

      this.table = {
        ...this.schema,
        toDoListConfig: true,
      };

      // Set table validation rule params
      this.validationRuleFields.operation.data.options = [
        ...this.validationRuleFields.operation.data.options,
        ...this.validationRuleFields.nonRightHandSideOperations,
      ];
      this.validationRules = [...this.table.validationRules.ValidationRules].map(rule => {
        rule.LogicTree.Expressions = rule.LogicTree.Expressions.map(exp => {
          if (!exp.RightHandSide) {
            return {
              ...exp,
              RightHandSide: {value: null, ExpressionItemType: 'fieldGuid'},
            };
          }
          return exp;
        });
        return rule;
      });

      this.getFormattedValidationRulesData({type: 'unescape'});
      this.manualConfirmationForm.requiresManualConfirmation = this.table.validationRules.RequiresManualConfirmation;

      this.setFieldTypeMap();
    } else {
      this.table.entityGuid = uuidv4();
      this.showTableDetails = true;
      this.isNewTable = true;
    }
    await this.getFolders(this.$route.params.moduleUuid);
    await this.getOrchestration();
    this.postedStatus.value = this.table.postedStatusMDMFieldGuid !== '00000000-0000-0000-0000-000000000000';
    this.initialData = {...cloneDeep(this.table)};

    this.eventListeners();
  },
  beforeRouteEnter(to, from, next) {
    const rights = localStorage.getItem('table_rights');
    const isEditableTable = ['admin', 'edit'].includes(rights);
    if (isEditableTable) {
      next();
    } else {
      next(false);
    }
  },
  async beforeRouteLeave(to, from, next) {
    const {fields, ...tableData} = this.table;
    const {fields: initialFields, ...initialTableData} = this.initialData;

    if ((isEqual(tableData, initialTableData) && !(this.changedEntityGuids.length > 0)) || to.name === 'dataManagementBuilder.update') {
      return next();
    }

    const confirm = await this.$confirm(
      'Redirecting...',
      'Changes are not saved. Are you sure you want to continue?',
    );
    if (confirm) {
      next();
    }
  },
  destroyed() {
    // this[SET_TABLE_SCHEMA](SCHEMA_INITIAL_DATA);
    this.resetTableData();
    this[UNSET_FOLDER_LIST]();
  },

};
</script>

<style lang="scss" scoped>
@import "../../../../scss/variables";


.page-content {
  padding-top: 0 !important;
}

.width-20 {
  width: 20px;
}

.width-25 {
  width: 25px;
}

.width-40 {
  width: 40px;
}

.fields {
  div {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

.chevron-position {
  float: right;
}

.card-columns {
  & /deep/ .card-body {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .fields-header {
    display: flex;
    justify-content: space-between;
    padding-left: $table-builder-field-handle-width + 10px;
    padding-right: $table-builder-field-settings-width + $table-builder-field-delete-width + 35px;
  }
}

.table-details {
  .card-header {
    cursor: pointer;
  }
}

.table-wrapper {
  table {
    border-collapse: collapse; /* make the table borders collapse to each other */
    width: 100%;
  }
}

.items-gap {
  gap: 10px;
}

.icon-select-input {
  display: flex;
  align-items: center;
  gap: 10px;


  & .selected-icon {
    width: 20px;
    height: 20px;
    padding: 5px;
    border: 1px solid #ced4da;
  }
}

.icon__wrapper {
  margin-top: 20px;
  margin-bottom: 10px;
  p {
    margin-bottom: 0.5rem;
  }
}

</style>
